import { useRef, useCallback } from "react";
import { Text } from "@mantine/core";
import { modals } from "@mantine/modals";

/**
 * @typedef {Object} UseDeleteConfirmOptions
 * @property {string} [modalTitle] - Override the full modal title. Defaults to `"Delete {title}?"`.
 * @property {string} [message] - Override the modal body text. Defaults to `"Are you sure? This action cannot be undone."`.
 * @property {string} [confirmLabel] - Override the confirm button label. Defaults to `"Delete"`.
 */

/**
 * Hook that returns a stable function to open a delete confirmation modal.
 *
 * Uses `useRef` internally so the returned callback always calls the latest
 * `mutate` reference — no stale closures, no race conditions.
 *
 * @param {string} title - Entity name displayed in the modal title (e.g. `"speaker"` → `"Delete speaker?"`).
 * @param {function} mutate - The `mutate` function from a `useDelete*` hook.
 * @param {UseDeleteConfirmOptions} [options={}] - Optional modal customization.
 * @returns {(data?: object, mutateOptions?: import('@tanstack/react-query').MutateOptions) => void}
 *   A stable callback. Call with the data to pass to `mutate` (e.g. `{ id }`)
 *   and optional React Query `mutateOptions` (`{ onSuccess, onError }`).
 *
 * @example
 * // Pattern A — ID passed at call time
 * const { mutate } = useDeleteSpeaker();
 * const confirmDelete = useDeleteConfirm("speaker", mutate);
 * <ActionIcon onClick={() => confirmDelete({ id: speaker.id })} />
 *
 * @example
 * // Pattern B — ID already in hook params
 * const { mutate } = useDeleteRegistrationTicketType(data.id);
 * const confirmDelete = useDeleteConfirm("ticket type", mutate);
 * <ActionIcon onClick={() => confirmDelete()} />
 *
 * @example
 * // Custom options
 * const confirmDelete = useDeleteConfirm("workspace", mutate, {
 *   modalTitle: "Remove workspace permanently?",
 *   message: "All events will also be deleted.",
 *   confirmLabel: "Remove",
 * });
 */
export function useDeleteConfirm(title, mutate, options = {}) {
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  return useCallback(
    (data = {}, mutateOptions) => {
      const opts = optionsRef.current;
      modals.openConfirmModal({
        title: opts.modalTitle || `Delete ${title}?`,
        centered: true,
        children: (
          <Text size="sm">
            {opts.message ||
              `Are you sure you want to delete this ${title}? This action cannot be undone.`}
          </Text>
        ),
        labels: { confirm: opts.confirmLabel || "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: () => mutateRef.current(data, mutateOptions),
      });
    },
    [title]
  );
}
